import { VehicleWidgetDialogName } from 'Components/names';
import DialogButton, { type DialogButtonTemplateParams } from 'Components/smallComponents/dialogButton';
import type {
  TemplateContextMap,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { vehicleDiscardingRequested, vehicleSelected } from 'Core/actions/fitmentSearch/index';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';

import type { VehicleTemplateData } from './types';

interface Props {
  template: TemplateFunction<SingleVehicleGarageTemplateParams>;
  withCurrentVehicle?: boolean;
}

interface VehicleTemplateFields extends TemplateContextMap, VehicleTemplateData {
  template: 'active' | 'locked';
}

interface SingleVehicleGarageTemplateParams extends VehicleTemplateFields {
  changeButton: TemplateFunctionInvoker<DialogButtonTemplateParams>;
  discardVehicle: () => void;
  goToResults: () => void;
}

export default function SingleVehicleGarage({ template, withCurrentVehicle = true }: Props): TemplateResult {
  const dispatch = useDispatch();

  const selectedVehicle = useSelector(selectedVehicleSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  const discardVehicle = () => {
    dispatch(vehicleDiscardingRequested());
    fitmentSearchConfig.onVehicleDiscarded();
  };
  const goToResults = () => {
    dispatch(vehicleSelected(selectedVehicle));
    fitmentSearchConfig.onVehicleSelected(selectedVehicle.filteredFieldMap);
  };

  const templateName: VehicleTemplateFields['template'] = selectedVehicle.notNull ? 'locked' : 'active';

  const templateParams = {
    vehicle: selectedVehicle.filteredFieldMap,
    vehicleString: selectedVehicle.toString(),
    vehicleBaseValues: selectedVehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: selectedVehicle.getFilteredValues(extraFitmentFields),
    template: templateName,
  } satisfies VehicleTemplateFields;

  const changeButton = (templ: TemplateFunction<DialogButtonTemplateParams>) => {
    const props = {
      template: templ,
      templateParams,
      dialogName: VehicleWidgetDialogName,
      metadata: { withCurrentVehicle },
    };
    return <DialogButton {...props} key="change-button" />;
  };

  return template.call({ changeButton, discardVehicle, goToResults, ...templateParams });
}
